import React, { useState } from "react";
import { Phone, Mail, MessageSquare, Clock, CheckCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { saveLead } from "../lib/contactService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industryOptions = [
  "Education",
  "Healthcare",
  "Real Estate",
  "Financial Services",
  "Retail & Commerce",
  "Hospitality & Restaurants",
  "Other",
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    industry: "",
    requirement: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await saveLead(formData);
      setSuccess(true);
      setFormData({ name: "", company: "", email: "", phone: "", industry: "", requirement: "" });
    } catch (err) {
      setError("Something went wrong. Please try calling us directly at +91 99588 91551.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "form-input w-full bg-[#0A0A0A] border border-white/8 rounded-xl px-4 py-3.5 text-white text-sm placeholder-[#52525B] transition-all duration-200 focus:border-[#00E599]/50 focus:outline-none focus:ring-0 focus:shadow-[0_0_0_3px_rgba(0,229,153,0.08)]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#050505] relative overflow-hidden"
    >
      {/* Background accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 20% 80%, rgba(0, 229, 153, 0.05) 0%, transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left Info */}
          <div className="lg:col-span-2">
            <span className="text-xs font-bold text-[#00E599] uppercase tracking-widest block mb-4">
              Get In Touch
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-white mb-5"
              style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.025em" }}
            >
              Book Free AI Consultation
            </h2>
            <p className="text-[#A1A1AA] leading-relaxed mb-10">
              Talk to our AI experts and discover how we can transform your business operations
              with purpose-built AI systems.
            </p>

            {/* Contact Details */}
            <div className="space-y-5 mb-10">
              <a
                href="tel:+919958891551"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#00E599]/8 border border-[#00E599]/15 flex items-center justify-center group-hover:bg-[#00E599]/15 transition-colors">
                  <Phone size={18} className="text-[#00E599]" />
                </div>
                <div>
                  <p className="text-xs text-[#71717A] mb-0.5">Call / WhatsApp</p>
                  <p className="text-sm font-medium text-white">+91 99588 91551</p>
                </div>
              </a>

              <a
                href="https://wa.me/919958891551?text=Hi%20CodeIntel%20AI%20Labs,%20I%20am%20interested%20in%20AI%20automation,%20AI%20agents,%20or%20a%20custom%20AI%20solution%20for%20my%20business.%20Let's%20discuss."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#25D366]/8 border border-[#25D366]/15 flex items-center justify-center group-hover:bg-[#25D366]/15 transition-colors">
                  <MessageSquare size={18} className="text-[#25D366]" />
                </div>
                <div>
                  <p className="text-xs text-[#71717A] mb-0.5">WhatsApp Chat</p>
                  <p className="text-sm font-medium text-white">Start a Conversation</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#00E599]/8 border border-[#00E599]/15 flex items-center justify-center">
                  <Clock size={18} className="text-[#00E599]" />
                </div>
                <div>
                  <p className="text-xs text-[#71717A] mb-0.5">Response Time</p>
                  <p className="text-sm font-medium text-white">Within 2 hours (Business Days)</p>
                </div>
              </div>
            </div>

            {/* What to expect */}
            <div className="p-5 rounded-2xl bg-[#0A0A0A] border border-white/6">
              <p className="text-xs font-semibold text-[#00E599] uppercase tracking-widest mb-3">
                What to Expect
              </p>
              {[
                "Free 30-min AI strategy session",
                "Industry-specific AI roadmap",
                "No-obligation proposal",
                "Direct access to our AI team",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 mb-2.5 last:mb-0">
                  <CheckCircle size={13} className="text-[#00E599] flex-shrink-0" />
                  <span className="text-xs text-[#A1A1AA]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#0A0A0A] border border-white/6 rounded-2xl p-8 md:p-10">
              {success ? (
                <div
                  data-testid="contact-success-msg"
                  className="flex flex-col items-center justify-center h-full py-12 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-[#00E599]/10 border border-[#00E599]/25 flex items-center justify-center mb-5">
                    <CheckCircle size={30} className="text-[#00E599]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Message Received!
                  </h3>
                  <p className="text-[#A1A1AA] text-sm mb-6 max-w-sm">
                    Thank you for reaching out. Our AI experts will contact you within 2 hours during business hours.
                  </p>
                  <a
                    href="https://wa.me/919958891551?text=Hi%20CodeIntel%20AI%20Labs,%20I%20am%20interested%20in%20AI%20automation,%20AI%20agents,%20or%20a%20custom%20AI%20solution%20for%20my%20business.%20Let's%20discuss."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-black bg-[#00E599] rounded-full px-6 py-3"
                  >
                    Chat on WhatsApp Now
                  </a>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                        Full Name <span className="text-[#00E599]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                        data-testid="contact-input-name"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        data-testid="contact-input-company"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                        Email Address <span className="text-[#00E599]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        required
                        data-testid="contact-input-email"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 ..."
                        data-testid="contact-input-phone"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                      Industry <span className="text-[#00E599]">*</span>
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      data-testid="contact-select-industry"
                      className={`${inputClass} cursor-pointer`}
                      style={{ backgroundColor: "#0A0A0A" }}
                    >
                      <option value="" disabled>Select your industry</option>
                      {industryOptions.map((opt) => (
                        <option key={opt} value={opt} style={{ backgroundColor: "#0A0A0A" }}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                      Project Requirement <span className="text-[#00E599]">*</span>
                    </label>
                    <textarea
                      name="requirement"
                      value={formData.requirement}
                      onChange={handleChange}
                      placeholder="Describe what you're looking to build or automate..."
                      required
                      rows={4}
                      data-testid="contact-input-requirement"
                      className={`${inputClass} resize-none`}
                    />
                  </div>

                  {error && (
                    <p
                      data-testid="contact-error-msg"
                      className="text-sm text-red-400 bg-red-500/8 border border-red-500/15 rounded-xl p-3"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full btn-primary-glow rounded-xl py-4 font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <>
                        Book Free AI Consultation
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#52525B] text-center">
                    No spam. We'll only contact you about your inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
